import { Fragment } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { XMarkIcon, DocumentTextIcon, CodeBracketIcon, CubeIcon } from '@heroicons/react/24/outline'
import { colorMap, formatDate } from '../../Common'

const ModalVisualizacaoServico = ({ isOpen, onClose, servico, color = 'blue' }) => {
    const colorClasses = colorMap[color] || colorMap.blue

    if (!servico) return null

    return (
        <Transition.Root show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="relative transform overflow-hidden rounded-2xl bg-white px-4 pb-6 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:p-6">
                                <div className="absolute right-0 top-0 pr-4 pt-4">
                                    <button
                                        type="button"
                                        className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-cyan focus:ring-offset-2 transition-colors"
                                        onClick={onClose}
                                    >
                                        <span className="sr-only">Fechar</span>
                                        <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                                    </button>
                                </div>

                                <div className="border-b border-gray-200 pb-4">
                                    <div className="inline-flex items-center justify-center w-full px-4 py-2 rounded-lg bg-gray-50 mb-3">
                                        <span className="text-sm font-semibold text-gray-700">
                                            👁️ Visualização
                                        </span>
                                    </div>
                                    <h3 className="text-2xl font-bold leading-7 text-center text-gray-900">
                                        Detalhes do Serviço
                                    </h3>
                                    <p className="mt-2 text-sm text-gray-500 text-center">
                                        Informações completas do serviço cadastrado
                                    </p>
                                </div>

                                <div className="mt-6 space-y-8">
                                    <div className="border-b border-gray-200 pb-8">
                                        <div className="flex items-center gap-3 mb-4">
                                            <DocumentTextIcon className="h-5 w-5 text-brand-cyan" />
                                            <h4 className="text-base font-semibold text-gray-900">Informações Básicas</h4>
                                        </div>
                                        <div className="mt-4 space-y-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-500">
                                                    Nome do Serviço
                                                </label>
                                                <p className="mt-1 text-base font-semibold text-gray-900">
                                                    {servico.nome}
                                                </p>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-500">
                                                    Descrição
                                                </label>
                                                <p className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">
                                                    {servico.descricao}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="border-b border-gray-200 pb-8">
                                        <div className="flex items-center gap-3 mb-4">
                                            <CodeBracketIcon className="h-5 w-5 text-brand-cyan" />
                                            <h4 className="text-base font-semibold text-gray-900">Informações Adicionais</h4>
                                        </div>
                                        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-6">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-500">
                                                    Código do Serviço
                                                </label>
                                                <p className="mt-1 text-base font-semibold text-gray-900 font-mono">
                                                    {servico.codigo_servico}
                                                </p>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-500">
                                                    Municipal
                                                </label>
                                                <div className="mt-1">
                                                    <span className={`inline-flex items-center px-3 py-2 rounded-lg text-sm font-semibold ${servico.municipal === 'true' ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'}`}>
                                                        {servico.municipal === 'true' ? 'Sim' : 'Não'}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div>
                                        <div className="flex items-center gap-3 mb-4">
                                            <CubeIcon className="h-5 w-5 text-brand-cyan" />
                                            <h4 className="text-base font-semibold text-gray-900">Informações do Sistema</h4>
                                        </div>
                                        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-6">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-500">
                                                    Data de Criação
                                                </label>
                                                <p className="mt-1 text-sm font-semibold text-gray-900">
                                                    {formatDate(servico.data_criacao)}
                                                </p>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-500">
                                                    Última Atualização
                                                </label>
                                                <p className="mt-1 text-sm font-semibold text-gray-900">
                                                    {formatDate(servico.data_atualizacao)}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-8 flex items-center justify-end border-t border-gray-200 pt-6">
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        className={`rounded-lg px-6 py-2.5 text-sm font-semibold text-white shadow-sm ${colorClasses.headerButtonBg} ${colorClasses.headerButtonHover} transition-all`}
                                    >
                                        Fechar
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    )
}

export default ModalVisualizacaoServico

